import { createGlobalStyle } from 'styled-components';

export const primaryColor = '#428DE7';
export const secondaryColor = '#3276ca';

const GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    background-color: #f9fbfd;
    overflow-x: hidden;
    transition: all 0.3s ease;
  }

  .container {
    width: 90%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-bottom: 0.5rem;
    font-weight: 600;
    line-height: 1.3;
  }

  p {
    margin-bottom: 1rem;
  }

  ul {
    list-style: none;
  }

  a {
    text-decoration: none;
    color: ${primaryColor};
    transition: all 0.3s ease;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  button, input, textarea, select {
    font-family: inherit;
  }

  section {
    padding: 5rem 0;
  }

  /* Animations */
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes slideDown {
    from { transform: translateY(-30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes slideLeft {
    from { transform: translateX(30px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes slideRight {
    from { transform: translateX(-30px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  /* Utility Classes */
  .animate-fade-in {
    animation: fadeIn 0.8s ease-out forwards;
  }

  .animate-slide-up {
    animation: slideUp 0.8s ease-out forwards;
  }

  .animate-slide-down {
    animation: slideDown 0.8s ease-out forwards;
  }

  .animate-slide-left {
    animation: slideLeft 0.8s ease-out forwards;
  }

  .animate-slide-right {
    animation: slideRight 0.8s ease-out forwards;
  }

  .animate-pulse {
    animation: pulse 2s infinite;
  }

  .delay-100 { animation-delay: 100ms; }
  .delay-200 { animation-delay: 200ms; }
  .delay-300 { animation-delay: 300ms; }
  .delay-400 { animation-delay: 400ms; }
  .delay-500 { animation-delay: 500ms; }

  /* Button styles */
  .btn {
    display: inline-block;
    padding: 0.8rem 1.8rem;
    background-color: ${primaryColor};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    text-align: center;
    
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: all 0.5s ease;
    }
    
    &:hover {
      background-color: ${secondaryColor};
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(50, 118, 202, 0.4);
      
      &:after {
        left: 100%;
      }
    }
    
    &:active {
      transform: translateY(-1px);
    }
  }

  .btn-outline {
    background-color: transparent;
    color: ${primaryColor};
    border: 2px solid ${primaryColor};
    
    &:hover {
      background-color: ${primaryColor};
      color: white;
    }
  }

  .btn-secondary {
    background-color: #6c757d;
    
    &:hover {
      background-color: #5a6268;
    }
  }

  .btn-success {
    background-color: #28a745;
    
    &:hover {
      background-color: #218838;
    }
  }

  /* Card styles */
  .card {
    background-color: white;
    border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    padding: 1.5rem;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    }
  }

  /* Hover effects */
  .hover-zoom {
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
    }
  }

  .hover-lift {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    }
  }

  /* Scroll reveal classes */
  .reveal {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s ease;
  }

  .reveal.active {
    opacity: 1;
    transform: translateY(0);
  }

  /* Grid layouts */
  .grid {
    display: grid;
    gap: 2rem;
  }

  .grid-2 {
    grid-template-columns: repeat(1, 1fr);
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .grid-3 {
    grid-template-columns: repeat(1, 1fr);
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (min-width: 992px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .grid-4 {
    grid-template-columns: repeat(1, 1fr);
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (min-width: 992px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  /* Responsive text classes */
  .text-center {
    text-align: center;
  }

  @media (min-width: 768px) {
    .text-md-left {
      text-align: left;
    }
    
    .text-md-center {
      text-align: center;
    }
    
    .text-md-right {
      text-align: right;
    }
  }

  /* Spacing utilities */
  .mt-1 { margin-top: 0.5rem; }
  .mt-2 { margin-top: 1rem; }
  .mt-3 { margin-top: 1.5rem; }
  .mt-4 { margin-top: 2rem; }
  .mt-5 { margin-top: 3rem; }

  .mb-1 { margin-bottom: 0.5rem; }
  .mb-2 { margin-bottom: 1rem; }
  .mb-3 { margin-bottom: 1.5rem; }
  .mb-4 { margin-bottom: 2rem; }
  .mb-5 { margin-bottom: 3rem; }

  .py-1 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
  .py-2 { padding-top: 1rem; padding-bottom: 1rem; }
  .py-3 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
  .py-4 { padding-top: 2rem; padding-bottom: 2rem; }
  .py-5 { padding-top: 3rem; padding-bottom: 3rem; }

  .px-1 { padding-left: 0.5rem; padding-right: 0.5rem; }
  .px-2 { padding-left: 1rem; padding-right: 1rem; }
  .px-3 { padding-left: 1.5rem; padding-right: 1.5rem; }
  .px-4 { padding-left: 2rem; padding-right: 2rem; }
  .px-5 { padding-left: 3rem; padding-right: 3rem; }
`;

export default GlobalStyles; 